import { Check } from 'lucide-react';
import { useDemoModal } from '../demo-modal-context';
import { cn } from '../../lib/cn';

const TIERS = [
  {
    name: 'Starter',
    deliveries: 'Up to 200 / month',
    monthly: '$499',
    annual: '$5,489',
    cta: 'Request a demo',
    featured: false,
  },
  {
    name: 'Growth',
    deliveries: 'Up to 500 / month',
    monthly: '$999',
    annual: '$10,989',
    cta: 'Request a demo',
    featured: true,
  },
  {
    name: 'Professional',
    deliveries: 'Up to 1,000 / month',
    monthly: '$1,499',
    annual: '$16,489',
    cta: 'Request a demo',
    featured: false,
  },
  {
    name: 'Enterprise',
    deliveries: '1,000+ / month',
    monthly: 'Custom',
    annual: 'Custom',
    cta: 'Contact sales',
    featured: false,
  },
] as const;

export function Pricing() {
  const { open } = useDemoModal();
  return (
    <section id="pricing" className="bg-white py-24 md:py-32">
      <div className="container-page">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">Pricing</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-ink md:text-4xl">
            Per-tenant subscription. Combined volume across all your pharmacies.
          </h2>
          <p className="mt-4 text-base text-ink-2">
            Annual billing equivalent to 11 months. Pay monthly or annually.
          </p>
        </div>

        <div className="mt-16 grid gap-4 lg:grid-cols-4">
          {TIERS.map((tier) => (
            <div
              key={tier.name}
              className={cn(
                'relative flex flex-col rounded-2xl border bg-white p-6 shadow-sm',
                tier.featured ? 'border-primary ring-1 ring-primary' : 'border-border',
              )}
            >
              {tier.featured ? (
                <span className="absolute -top-3 left-6 rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                  Most popular
                </span>
              ) : null}
              <h3 className="text-base font-semibold text-ink">{tier.name}</h3>
              <p className="mt-1 text-sm text-ink-2">{tier.deliveries}</p>
              <div className="mt-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-semibold tracking-tight text-ink">{tier.monthly}</span>
                  {tier.monthly.startsWith('$') ? (
                    <span className="text-sm text-ink-3">/ month</span>
                  ) : null}
                </div>
                <p className="mt-1 text-xs text-ink-3">
                  {tier.annual === 'Custom' ? 'Custom annual' : `${tier.annual} annual (11 months)`}
                </p>
              </div>

              <button
                type="button"
                onClick={open}
                className={cn(
                  'mt-6 inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium shadow-sm transition-colors',
                  tier.featured
                    ? 'bg-primary text-white hover:bg-primary-dark'
                    : 'bg-ink text-white hover:bg-ink/90',
                )}
              >
                {tier.cta}
              </button>
            </div>
          ))}
        </div>

        <div className="mx-auto mt-12 max-w-2xl space-y-3 text-center text-sm text-ink-2">
          <p className="flex items-center justify-center gap-2">
            <Check className="h-4 w-4 text-primary" />
            Every tier includes the full platform. No feature gating.
          </p>
          <p className="flex items-center justify-center gap-2">
            <Check className="h-4 w-4 text-primary" />
            Onboarding included for the first three customers. Subsequent: $500–$2,500 based on operation size.
          </p>
        </div>
      </div>
    </section>
  );
}
